import { ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import type { Webhook } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const useWebhooksStore = defineStore("webhooks", () => {
  const webhooks = ref<Webhook[]>([]);

  const fetchWebhooks = async () => {
    const { data } = await useMyFetch("/webhooks").json<{ webhooks: Webhook[] }>();
    webhooks.value = data.value?.webhooks || [];
    return webhooks.value;
  };

  const createWebhook = async (payload: Partial<Webhook>) => {
    const { data, error } = await useMyFetch("/webhooks").post({ webhook: payload }).json<Webhook>();
    if (error.value) throw error.value;

    captureEvent("webhook_created", { webhook_id: data.value?.id });

    return data.value;
  };

  const updateWebhook = async (id: number, payload: Partial<Webhook>) => {
    const { data, error } = await useMyFetch(`/webhooks/${id}`).put({ webhook: payload }).json<Webhook>();
    if (error.value) throw error.value;

    captureEvent("webhook_updated", { webhook_id: id });

    return data.value;
  };

  const deleteWebhook = async (id: number) => {
    const { error } = await useMyFetch(`/webhooks/${id}`).delete();
    if (error.value) throw error.value;
    captureEvent("webhook_deleted", { webhook_id: id });

    webhooks.value = webhooks.value.filter((webhook) => webhook.id !== id);
  };

  return {
    webhooks,
    fetchWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
  };
});
